import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/hooks/useAuth';

export default function LoginPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!loading && user) {
      navigate('/admin', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      setStatus('error');
      setErrorMsg('E-mail ou senha incorretos. Verifique suas credenciais.');
    } else {
      navigate('/admin', { replace: true });
    }
  };

  if (loading) return null;

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'linear-gradient(135deg, #0B2F5B 0%, #123E73 60%, #0B2F5B 100%)' }}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-10"
          style={{ background: '#C9A24A' }}
        />
        <div
          className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full opacity-10"
          style={{ background: '#C9A24A' }}
        />
      </div>

      <div className="relative w-full max-w-md">
        {/* Card */}
        <div className="rounded-2xl p-8 md:p-10" style={{ background: '#FFFFFF' }}>
          {/* Logo / Brand */}
          <div className="text-center mb-8">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ background: 'linear-gradient(135deg, #0B2F5B, #123E73)' }}
            >
              <i className="ri-shield-keyhole-line text-2xl" style={{ color: '#C9A24A' }} />
            </div>
            <h1 className="font-serif text-2xl font-bold mb-1" style={{ color: '#0B2F5B' }}>
              Área Administrativa
            </h1>
            <p className="text-sm" style={{ color: '#1A1A1A', opacity: 0.5 }}>
              Acesso restrito a administradores
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            {/* Email */}
            <div>
              <label className="block text-xs font-semibold mb-2" style={{ color: '#0B2F5B' }}>
                E-mail
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                  <i className="ri-mail-line text-base" style={{ color: '#0B2F5B', opacity: 0.4 }} />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="admin@exemplo.com"
                  className="w-full h-12 pl-11 pr-4 rounded-xl text-sm outline-none transition-all duration-200"
                  style={{ background: '#F8F9FD', border: '2px solid rgba(11,47,91,0.12)', color: '#1A1A1A' }}
                  onFocus={(e) => { e.target.style.borderColor = '#C9A24A'; e.target.style.boxShadow = '0 0 0 3px rgba(201,162,74,0.12)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'rgba(11,47,91,0.12)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold mb-2" style={{ color: '#0B2F5B' }}>
                Senha
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                  <i className="ri-lock-line text-base" style={{ color: '#0B2F5B', opacity: 0.4 }} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full h-12 pl-11 pr-12 rounded-xl text-sm outline-none transition-all duration-200"
                  style={{ background: '#F8F9FD', border: '2px solid rgba(11,47,91,0.12)', color: '#1A1A1A' }}
                  onFocus={(e) => { e.target.style.borderColor = '#C9A24A'; e.target.style.boxShadow = '0 0 0 3px rgba(201,162,74,0.12)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'rgba(11,47,91,0.12)'; e.target.style.boxShadow = 'none'; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center cursor-pointer"
                  style={{ color: '#0B2F5B', opacity: 0.4 }}
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            {/* Error message */}
            {status === 'error' && (
              <div
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm"
                style={{ background: 'rgba(220,38,38,0.07)', color: '#dc2626' }}
              >
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-error-warning-line" />
                </div>
                {errorMsg}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full h-12 rounded-xl font-bold text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap transition-all duration-300 hover:opacity-90 disabled:opacity-60 mt-1"
              style={{ background: 'linear-gradient(135deg, #0B2F5B, #123E73)', color: '#FFFFFF' }}
            >
              {status === 'loading' ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Entrando...
                </>
              ) : (
                <>
                  <i className="ri-login-box-line" />
                  Entrar
                </>
              )}
            </button>
          </form>

          {/* Back to site */}
          <div className="mt-6 text-center">
            <a
              href="/"
              className="text-xs font-medium cursor-pointer transition-opacity hover:opacity-80"
              style={{ color: '#0B2F5B', opacity: 0.45 }}
            >
              <i className="ri-arrow-left-line mr-1" />
              Voltar ao site
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
